import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/useAuth';
import { formatCurrency } from '@/lib/utils';
import { useEstimateData } from '@/pages/estimates/hooks/useEstimateData';
import { useEstimateFilterBuilder } from '@/pages/estimates/hooks/useEstimateFilterBuilder';
import { useViewPreference } from '@/hooks/useViewPreference';
import { FilterBuilder } from '@/components/invoices/FilterBuilder';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, FileText, Eye, Plus } from 'lucide-react';
import { useNotify } from '@/hooks/useNotify';
import EstimateActions from '@/components/estimates/components/EstimateActions';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import DataTableWrapper from '@/components/shared/DataTableWrapper';
import ViewToggle from '@/components/shared/ViewToggle';
import FilterToolbar from '@/components/shared/FilterToolbar';
import EstimateStatusBadge from './components/EstimateStatusBadge';
import EstimateCard from './EstimateCard';

type EstimateStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled';

const EstimatesList = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const {
    estimates,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
  } = useEstimateData();
  const filterBuilder = useEstimateFilterBuilder(estimates);
  const { viewMode, setViewMode } = useViewPreference({ entityType: 'estimates', defaultView: 'list' });

  // Update expired estimates
  const updateExpiredEstimates = async () => {
    try {
      const { error } = await supabase.rpc('update_expired_estimates');
      if (error) throw error;
      
      notify.success('Expired estimates updated');
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
    } catch (error) {
      notify.error(error);
    }
  };

  return (
    <PageLayout>
      <PageHeader 
        title="Estimates"
        description="Manage your project estimates"
      />

      {/* Estimates Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              All Estimates
            </CardTitle>
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
          
          {/* Unified FilterToolbar */}
          <FilterToolbar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search estimates..."
            sortOptions={[
              { value: 'created_at', label: 'Date Created' },
              { value: 'estimate_number', label: 'Estimate Number' },
              { value: 'title', label: 'Title' },
              { value: 'total_amount', label: 'Total Amount' },
              { value: 'expires_at', label: 'Expires Date' },
            ]}
            sortValue={sortBy}
            onSortChange={setSortBy}
            sortDir={sortDir}
            onSortDirChange={setSortDir}
            actions={
              <Button asChild size="sm" className="gap-1.5">
                <Link to="/estimates/create">
                  <Plus className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline truncate">New Estimate</span>
                  <span className="sm:hidden truncate">New</span>
                </Link>
              </Button>
            }
            advancedFilter={
              <FilterBuilder
                draftGroups={filterBuilder.draftGroups}
                appliedGroups={filterBuilder.appliedGroups}
                fields={filterBuilder.filterFields}
                entities={filterBuilder.filterEntities}
                attributesByEntity={filterBuilder.filterAttributesByEntity}
                onAddGroup={filterBuilder.addGroup}
                onUpdateGroup={filterBuilder.updateGroup}
                onRemoveGroup={filterBuilder.removeGroup}
                onAddRuleToGroup={filterBuilder.addRuleToGroup}
                onUpdateRuleInGroup={filterBuilder.updateRuleInGroup}
                onRemoveRuleFromGroup={filterBuilder.removeRuleFromGroup}
                onClearAll={filterBuilder.clearAllRules}
                onApplyFilters={filterBuilder.applyFilters}
                hasActiveFilters={filterBuilder.hasActiveFilters}
                hasUnappliedChanges={filterBuilder.hasUnappliedChanges}
                canApplyFilters={filterBuilder.canApplyFilters}
              />
            }
            hasAdvancedFilters={filterBuilder.hasActiveFilters}
            advancedFilterCount={filterBuilder.appliedGroups.reduce((c, g) => c + g.rules.filter(r => r.entity && r.attribute && r.value).length, 0)}
            onClearAdvancedFilters={filterBuilder.clearAllRules}
          />
        </CardHeader>
        <CardContent className={viewMode === 'list' ? 'p-0' : undefined}>
          <DataTableWrapper
            isEmpty={!filterBuilder.filteredEstimates || filterBuilder.filteredEstimates.length === 0}
            emptyState={{
              title: "No estimates found",
              description: "Get started by creating your first estimate.",
              action: (
                <Button asChild className="mt-4">
                  <Link to="/estimates/create">Create Your First Estimate</Link>
                </Button>
              )
            }}
            skeletonColumns={9}
          >
            {viewMode === 'list' ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="hidden sm:table-cell w-28">Estimate #</TableHead>
                    <TableHead className="max-w-0 w-full">Title</TableHead>
                    <TableHead className="hidden md:table-cell">Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Version</TableHead>
                    <TableHead className="hidden md:table-cell">Created</TableHead>
                    <TableHead className="hidden lg:table-cell">Expires</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Total</TableHead>
                    <TableHead className="text-right w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterBuilder.filteredEstimates?.map((estimate) => (
                    <TableRow key={estimate.id}>
                      <TableCell className="hidden sm:table-cell font-mono text-sm whitespace-nowrap w-28">
                        {estimate.estimate_number || 'N/A'}
                      </TableCell>
                      <TableCell className="max-w-0 w-full">
                        <div className="min-w-0">
                          <Link
                            to={`/estimates/${estimate.id}`}
                            className="font-medium hover:underline block truncate"
                          >
                            {estimate.title}
                          </Link>
                          <span className="sm:hidden text-xs text-muted-foreground font-mono">
                            {estimate.estimate_number || 'N/A'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {getCustomerDisplayName(estimate.customers)}
                      </TableCell>
                      <TableCell>
                        <EstimateStatusBadge 
                          status={estimate.status as EstimateStatus} 
                          expiresAt={estimate.expires_at}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">v{estimate.version}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {format(new Date(estimate.created_at), 'dd MMMM yyyy')}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {estimate.expires_at ? (
                          <span className={
                            new Date(estimate.expires_at) < new Date() 
                              ? 'text-destructive font-medium' 
                              : ''
                          }>
                            {format(new Date(estimate.expires_at), 'dd MMMM yyyy')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">No expiry</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap font-mono">
                        {formatCurrency(estimate.total_amount || 0)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" asChild>
                            <Link to={`/estimates/${estimate.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <EstimateActions
                            estimateId={estimate.id}
                            estimateTitle={estimate.title}
                            estimateStatus={estimate.status}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <EstimateCard estimates={filterBuilder.filteredEstimates || []} />
            )}
          </DataTableWrapper>
        </CardContent>
      </Card>
    </PageLayout>
  );
};

export default EstimatesList;
